import { useEffect, useState } from "react";

type Props = {
	shouldHold: boolean;
	holdSeconds: number;
	resetKey?: number;
	isPaused?: boolean;
};

// Holds for holdSeconds once shouldHold becomes true, ticking only while not
// paused. Holds synchronously on the first render after a resetKey change so
// consumers never see a one-render gap before the arming effect runs.
const usePausableCountdownWindow = ({
	shouldHold,
	holdSeconds,
	resetKey,
	isPaused,
}: Props): boolean => {
	const [hold, setHold] = useState<{
		key?: number;
		holding: boolean;
		remainingSeconds: number;
	}>({ holding: false, remainingSeconds: 0 });

	const isHolding = shouldHold && (hold.key === resetKey ? hold.holding : true);

	useEffect(() => {
		if (!shouldHold) {
			setHold({ holding: false, remainingSeconds: 0 });
			return;
		}
		setHold({ key: resetKey, holding: true, remainingSeconds: holdSeconds });
	}, [resetKey, shouldHold, holdSeconds]);

	useEffect(() => {
		if (!(hold.holding && hold.key === resetKey) || isPaused) {
			return;
		}
		const interval = setInterval(() => {
			setHold((prev) => {
				if (!prev.holding) {
					return prev;
				}
				const remainingSeconds = prev.remainingSeconds - 1;
				return remainingSeconds <= 0
					? { ...prev, holding: false, remainingSeconds: 0 }
					: { ...prev, remainingSeconds };
			});
		}, 1000);
		return () => clearInterval(interval);
	}, [hold.holding, hold.key, resetKey, isPaused]);

	return isHolding;
};

export default usePausableCountdownWindow;
